import { useState, useRef, useEffect, lazy, Suspense } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import classNames from "classnames";
import AccessibilitySkipLink from "../AccessibilitySkipLink/component";
import ExternalHeader from "./components/ExternalHeader/component";
import { normalizeClass } from "../../utils/path/library";
import PageActions from "../../utils/page/library";
import { EXTERNAL_HEADER_PATH, ACCOUNT_PATH } from "./constants";
import PrimaryNav from "./components/PrimaryNav/component";
import SecondaryNav from "./components/SecondaryNav/component";
import { PERSISTENT_PLAYER_HOME_SELECTOR } from "../PersistentPlayer/constants";
import "./styles/themes/_default.scss";
import ErrorBoundary from "../ErrorBoundary/component";
import importMF from "../../federated/importMF";
import { ACC_MODULES } from "../../federated/accountCentre";
import If from "../If/component";
import useIsClientHydrated from "../../hooks/useIsClientHydrated";
import NewsBar from "../NewsBar/component";
const AccountNav = lazy(() => importMF(ACC_MODULES.RemoteAccountNav));

const GlobalHeader = ({
    isDetail = false,
    viewport = "viewportLarge",
    route,
    maintenance = false,
}) => {
    const [menuOpen, setMenuOpen] = useState(false); //when the menu is open, the entire Global Header will have position fixed, so this state needs to be in the parent
    const isClientHydrated = useIsClientHydrated(); // Show account nav after hydration
    const baseNavSection = useSelector((state) => state.app.baseNavSection);
    const fixed = useSelector((state) => state.fixed);
    const a11yTopOfPageAnchorRef = useRef(null);
    const isPersistentPlayerActive = useSelector((state) => {
        return (
            state.persistentPlayer.isVisible &&
            state.persistentPlayer.selector === PERSISTENT_PLAYER_HOME_SELECTOR
        );
    });

    //Check if on cbc.ca/search and the query parameter exists
    const isSearchPageWithQuery = useSelector((state) => {
        const result =
            state.app.path === "search" &&
            state.search?.searchTerm !== undefined &&
            state.search?.searchTerm !== "";
        return result;
    });

    useEffect(() => {
        a11yTopOfPageAnchorRef.current?.focus();
    }, [isSearchPageWithQuery, route?.path]);

    const setMenuOpenCallback = (value) => {
        setMenuOpen(value);
        PageActions.updateBodyScrollState(value);
    };

    const renderAccessibilityLinkToPersistentPlayer = () => {
        if (isPersistentPlayerActive) {
            return (
                <AccessibilitySkipLink
                    linkText={"Skip to Media Player"}
                    linkHref={"#persistent-player"}
                    isGlobalSkipLink={true}
                />
            );
        }
        return null;
    };

    // Check if the Header is requested to be included by an external website via iframes
    if (fixed?.isWebView) return null;
    if (route?.path.startsWith(EXTERNAL_HEADER_PATH)) {
        return <ExternalHeader path={route.path} />;
    }

    const classes = classNames(
        "globalHeader",
        viewport,
        normalizeClass("", baseNavSection, "Header", false),
        {
            open: menuOpen,
            "introjs-fixParent":
                typeof document !== "undefined" &&
                document.body.classList.contains("onboarding"),
        }
    );

    // Check if the Header is for the Membership section
    const insideMembershipSection = route?.path.startsWith(ACCOUNT_PATH);

    return (
        <header className={classes} data-testid="header">
            <div className="viafoura" style={{ height: 0, minHeight: 0 }}>
                <vf-tray></vf-tray>
            </div>
            <a href="#content" className="a11y" ref={a11yTopOfPageAnchorRef}>
                Content
            </a>
            {insideMembershipSection ? (
                <>
                    <AccessibilitySkipLink
                        linkHref="#content"
                        displayHelpLink={true}
                        isGlobalSkipLink={true}
                    />
                    <ErrorBoundary
                        fallback={
                            <PrimaryNav
                                menuOpen={menuOpen}
                                setMenuOpen={setMenuOpenCallback}
                                viewport={viewport}
                                isFallback={true}
                            />
                        }
                    >
                        <If value={isClientHydrated}>
                            <Suspense>
                                <AccountNav
                                    path={route.path}
                                    search={route.search}
                                />
                            </Suspense>
                        </If>
                    </ErrorBoundary>
                </>
            ) : (
                <>
                    <AccessibilitySkipLink
                        linkHref={
                            isSearchPageWithQuery
                                ? "#searchMainContent"
                                : "#content"
                        }
                        displayHelpLink={true}
                        isGlobalSkipLink={true}
                    />
                    {renderAccessibilityLinkToPersistentPlayer()}
                    <PrimaryNav
                        menuOpen={menuOpen}
                        setMenuOpen={setMenuOpenCallback}
                        viewport={viewport}
                    />

                    {!maintenance && (
                        <SecondaryNav
                            viewport={viewport}
                            isDetail={isDetail}
                            menuOpen={menuOpen}
                        />
                    )}
                    {!menuOpen && <NewsBar />}
                </>
            )}
        </header>
    );
};

GlobalHeader.propTypes = {
    isDetail: PropTypes.bool,
    viewport: PropTypes.string,
    route: PropTypes.object,
};

export default GlobalHeader;
